import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  generatePost,
  fetchImagesFromSerper,
  generateGeminiImage,
} from "../api/postApi";
import { processImageToBase64 } from "../utils/imageUtils";
import { autoSaveGeneratedPost } from "../utils/firebaseUtils";

/**
 * Custom hook for post generation functionality
 * @param {Object} initialState - Initial state
 * @returns {Object} - Post generation methods and state
 */
const usePostGeneration = (initialState = {}) => {
  const navigate = useNavigate();
  const [isGeneratingPost, setIsGeneratingPost] = useState(false);
  const [isRegeneratingPost, setIsRegeneratingPost] = useState(false);
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [editedPostText, setEditedPostText] = useState("");
  const [hasEdited, setHasEdited] = useState(false);
  const [serperImages, setSerperImages] = useState([]);
  const [selectedSerperImages, setSelectedSerperImages] = useState([]);
  const [geminiGeneratedImage, setGeminiGeneratedImage] = useState(null);

  // Thêm state mới cho nhiều bài viết
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [selectedPostIndex, setSelectedPostIndex] = useState(0);

  // Auto-save tracking
  const [autoSavePostId, setAutoSavePostId] = useState(null);

  /**
   * Chuyển hướng đến trang nâng cấp gói dịch vụ
   */
  const redirectToUpgrade = () => {
    navigate("/upgrade");
  };

  /**
   * Check if user can create more posts (renamed from checkPoints)
   * @returns {boolean} - True if user can create more posts
   */
  const checkPostLimit = () => {
    return true;
  };

  /**
   * Generate a post
   * @param {Object} options - Post generation options
   * @param {string} userId - User ID
   * @param {boolean} allowNoImage - Whether to allow no image
   * @param {string} imageSource - Image source
   * @param {Array} contextFiles - Context files
   * @param {Function} incrementPostCount - Function to increment post count
   * @param {Object} refs - References to DOM elements
   * @returns {Promise<void>}
   */
  const generateNewPost = async (
    options,
    userId,
    allowNoImage,
    imageSource,
    contextFiles,
    incrementPostCount,
    refs = {}
  ) => {
    // Kiểm tra nếu người dùng đã chỉnh sửa bài viết
    if (generatedPost && hasEdited) {
      if (
        !window.confirm(
          "Bạn đã chỉnh sửa bài viết. Tạo bài viết mới sẽ xóa các chỉnh sửa của bạn. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }

    if (!checkPostLimit()) return;

    // Kiểm tra lại options.requirements
    if (!options.requirements || options.requirements.trim() === "") {
      // Nếu không có requirements nhưng có ảnh tải lên, thêm mô tả tạm thời
      const hasImageContext =
        contextFiles && contextFiles.some((file) => file.type === "image");
      if (hasImageContext) {
        options = {
          ...options,
          requirements: "Tạo bài viết dựa trên hình ảnh đã tải lên",
        };
      } else {
        console.error("Không thể tạo bài viết: Thiếu yêu cầu nội dung");
        toast.error(
          "Vui lòng nhập yêu cầu về nội dung bài viết trước khi tạo bài",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        return;
      }
    }

    // Xóa tất cả ảnh cũ
    setSelectedSerperImages([]);
    setGeminiGeneratedImage(null);
    setSerperImages([]);
    setIsEditMode(false); // Đảm bảo tắt chế độ sửa khi tạo bài viết mới
    setHasEdited(false); // Reset trạng thái đã chỉnh sửa

    // Reset các state bài viết
    setGeneratedPosts([]);
    setSelectedPostIndex(0);

    // Đặt imageSource về nguồn đã chọn hoặc 'none' nếu text-only
    const currentImageSource = allowNoImage ? "none" : imageSource;

    // Xử lý file context nếu có
    let contextData = [];
    if (contextFiles && contextFiles.length > 0) {
      try {
        // Chuyển đổi file thành base64
        contextData = await Promise.all(
          contextFiles.map(async (contextItem) => {
            try {
              // Xử lý dữ liệu ảnh
              if (contextItem.type === "image") {
                let imageContent;

                // Ưu tiên sử dụng preview nếu có
                if (contextItem.preview) {
                  try {
                    // Đảm bảo preview là base64 hợp lệ
                    imageContent = await processImageToBase64(
                      contextItem.preview
                    );
                    console.log(
                      `Đã xử lý preview của ảnh ${contextItem.name} thành base64 hợp lệ`
                    );
                  } catch (previewError) {
                    console.error(
                      `Lỗi khi xử lý preview của ảnh ${contextItem.name}:`,
                      previewError
                    );
                    // Nếu xử lý preview thất bại, thử xử lý file gốc
                    imageContent = await processImageToBase64(contextItem.file);
                  }
                } else if (contextItem.file) {
                  // Nếu không có preview, xử lý file gốc
                  imageContent = await processImageToBase64(contextItem.file);
                } else {
                  throw new Error(
                    `Không thể xử lý ảnh ${contextItem.name}: Không có dữ liệu hợp lệ`
                  );
                }

                // Kiểm tra kích thước sau khi đã xử lý
                if (imageContent && imageContent.length > 500000) {
                  console.log(
                    `Ảnh ${contextItem.name} vẫn quá lớn sau khi xử lý. Nén lại với chất lượng thấp hơn.`
                  );

                  try {
                    const { compressImage } = await import(
                      "../utils/imageUtils"
                    );
                    const blob = await fetch(imageContent).then((r) =>
                      r.blob()
                    );
                    const compressedBlob = await compressImage(
                      new File([blob], contextItem.name, { type: blob.type }),
                      { maxWidth: 500, maxHeight: 500, quality: 0.5 }
                    );

                    imageContent = await processImageToBase64(compressedBlob);
                  } catch (compressError) {
                    console.error(
                      `Lỗi khi nén thêm ảnh ${contextItem.name}:`,
                      compressError
                    );
                    // Nếu vẫn thất bại, thông báo không thể xử lý
                    return {
                      type: contextItem.type,
                      name: contextItem.name,
                      content: "Data quá lớn, không thể gửi đi",
                    };
                  }
                }

                return {
                  type: contextItem.type,
                  name: contextItem.name,
                  content: imageContent,
                };
              }
              // Xử lý dữ liệu văn bản
              else if (contextItem.type === "text") {
                // Nếu đã có preview là văn bản, sử dụng luôn
                if (
                  contextItem.preview &&
                  typeof contextItem.preview === "string"
                ) {
                  return {
                    type: contextItem.type,
                    name: contextItem.name,
                    content: contextItem.preview,
                  };
                }

                // Nếu không có preview, đọc từ file
                if (contextItem.file) {
                  const reader = new FileReader();
                  const text = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsText(contextItem.file);
                  });

                  return {
                    type: contextItem.type,
                    name: contextItem.name,
                    content: text,
                  };
                }

                throw new Error(
                  `Không thể xử lý file văn bản ${contextItem.name}: Không có dữ liệu hợp lệ`
                );
              }

              // Các loại file không được hỗ trợ
              return {
                type: contextItem.type,
                name: contextItem.name,
                content: "",
              };
            } catch (fileError) {
              console.error(`Lỗi xử lý file ${contextItem.name}:`, fileError);
              // Trả về object với content rỗng để tránh lỗi
              return {
                type: contextItem.type,
                name: contextItem.name,
                content: "",
              };
            }
          })
        );

        // Lọc bỏ các file không xử lý được hoặc content rỗng
        contextData = contextData.filter(
          (item) =>
            item.content &&
            item.content !== "Data quá lớn, không thể gửi đi" &&
            typeof item.content === "string"
        );

        if (contextData.length === 0 && contextFiles.length > 0) {
          toast.warning(
            "Không thể xử lý file do kích thước quá lớn hoặc định dạng không được hỗ trợ. Vui lòng sử dụng file nhỏ hơn.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        }
      } catch (error) {
        console.error("Lỗi khi xử lý file context:", error);
        toast.error(
          "Đã xảy ra lỗi khi xử lý file context. Vui lòng thử lại với file nhỏ hơn.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        setIsGeneratingPost(false);
        return;
      }
    }

    // Update options with context files
    const updatedOptions = {
      ...options,
      imageSource: currentImageSource,
      contextFiles: contextData.length > 0 ? contextData : undefined,
    };

    setIsGeneratingPost(true);

    try {
      // Gọi API tạo bài viết từ backend (chỉ tạo nội dung)
      const response = await generatePost(updatedOptions, userId);

      if (response.data.success) {
        // Kiểm tra cấu trúc response mới
        let posts = [];
        if (response.data.posts && Array.isArray(response.data.posts)) {
          // Cấu trúc mới: posts là trường trong response.data
          posts = response.data.posts;
        } else if (
          response.data.data &&
          response.data.data.posts &&
          Array.isArray(response.data.data.posts)
        ) {
          // Cấu trúc mới: posts là trường trong response.data.data
          posts = response.data.data.posts;
        } else if (
          response.data.data &&
          (response.data.data.title || response.data.data.content)
        ) {
          // Cấu trúc cũ: một bài viết duy nhất
          const { title, content } = response.data.data;
          posts = [
            {
              title: title || "",
              content: content || "",
              imageUrls: response.data.data.imageUrls || [],
            },
          ];
        } else {
          console.error(
            "Cấu trúc dữ liệu không đúng định dạng:",
            response.data
          );
          throw new Error("Dữ liệu không đúng định dạng");
        }

        // Lưu trữ các bài viết đã tạo
        const formattedPosts = posts.map((post) => ({
          text: post.content,
          title: post.title || post.content.substring(0, 50),
          image:
            post.imageUrls && post.imageUrls.length > 0
              ? post.imageUrls[0]
              : "",
        }));

        setGeneratedPosts(formattedPosts);

        // Đặt bài viết đầu tiên làm bài viết hiện tại
        if (formattedPosts.length > 0) {
          setGeneratedPost(formattedPosts[0]);
          setEditedPostText(formattedPosts[0].text);
        }

        // Increment post count - Gọi không đợi để cập nhật ngay lập tức
        if (incrementPostCount) {
          incrementPostCount().catch((error) => {
            console.error("❌ Lỗi khi tăng count tạo bài viết:", error);
          });
        }

        // Auto-save generated post - Gọi không đợi để tránh delay UI
        autoSaveGeneratedPost(
          {
            contextFiles: contextData,
            allowNoImage,
            imageSource: currentImageSource,
          },
          options,
          formattedPosts,
          "normal"
        ).then((result) => {
          if (result.success) {
            setAutoSavePostId(result.postId);
            console.log("✅ Bài viết được tự động lưu:", result.postId);
          } else {
            console.warn("⚠️ Không thể auto-save bài viết:", result.error);
          }
        }).catch((error) => {
          console.error("❌ Lỗi khi auto-save bài viết:", error);
        });

        // Xử lý ảnh chỉ khi không phải là bài viết text-only
        if (!allowNoImage) {
          if (currentImageSource === "internet") {
            // Nếu nguồn ảnh là internet, gọi API lấy ảnh từ Serper
            const firstPost = formattedPosts[0];
            await fetchSerperImages(firstPost.title || firstPost.text, userId);
          } else if (currentImageSource === "ai") {
            // Nếu nguồn ảnh là AI, gọi API tạo ảnh bằng Gemini
            const firstPost = formattedPosts[0];
            await generateAIImage(firstPost.title || firstPost.text, userId);
          }
          // Nếu là "upload" thì không làm gì ở đây, ảnh đã được xử lý riêng
        }

        // Thông báo thành công
        if (formattedPosts.length > 1) {
          toast.success(
            `Đã tạo ${formattedPosts.length} phiên bản bài viết. Bạn có thể chuyển đổi giữa các phiên bản.`,
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        } else {
          toast.success("Đã tạo bài viết thành công!", {
            position: "top-right",
            autoClose: 3000,
          });
        }

        // Cuộn xuống bài viết mới tạo
        setTimeout(() => {
          if (refs.generatedPostRef && refs.generatedPostRef.current) {
            refs.generatedPostRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } else {
        toast.error(
          response.data.message ||
            "Không thể tạo bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo bài viết:", error);

      // Kiểm tra nếu lỗi là do đạt giới hạn số lần tạo bài viết
      if (
        error.response &&
        error.response.data &&
        (error.response.data.error === "DAILY_LIMIT_REACHED" ||
          error.response.data.error === "DAILY_POST_LIMIT_REACHED")
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn đã đạt giới hạn tạo bài viết trong ngày. Vui lòng quay lại vào ngày mai.",
          {
            position: "top-right",
            autoClose: 7000,
          }
        );
      }
      // Xử lý lỗi từ validateUserSubscription
      else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần đăng ký gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION_EXPIRY"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần gia hạn gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "SUBSCRIPTION_EXPIRED"
      ) {
        toast.error(
          error.response.data.message ||
            "Gói dịch vụ của bạn đã hết hạn, vui lòng gia hạn.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.message &&
        error.message.includes("Không thể lấy thông tin từ URL tham khảo")
      ) {
        // Hiển thị thông báo lỗi cụ thể về URL tham khảo
        toast.error(error.message, {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        error.response.data.message.includes("URL")
      ) {
        // Hiển thị thông báo lỗi từ backend về URL
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (
        error.message &&
        error.message ===
          "Error: AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo"
      ) {
        // Hiển thị thông báo lỗi cụ thể về giới hạn bài viết
        toast.error(
          "AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        // Hiển thị thông báo lỗi cụ thể nếu có, nếu không thì hiển thị thông báo chung
        toast.error(
          error.message ||
            "Đã xảy ra lỗi khi tạo bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } finally {
      setIsGeneratingPost(false);
    }
  };

  /**
   * Chọn bài viết từ danh sách các bài viết đã tạo
   * @param {number} index - Vị trí của bài viết trong mảng
   */
  const selectPost = (index) => {
    if (index >= 0 && index < generatedPosts.length) {
      setSelectedPostIndex(index);
      const selectedPost = generatedPosts[index];
      setGeneratedPost(selectedPost);
      setEditedPostText(selectedPost.text);

      // Reset trạng thái chỉnh sửa
      setHasEdited(false);
      setIsEditMode(false);
    }
  };

  /**
   * Fetch images from Serper
   * @param {string} queryText - Query text
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  const fetchSerperImages = async (queryText, userId) => {
    setIsRegeneratingImage(true);
    try {
      const response = await fetchImagesFromSerper(queryText, userId);

      if (response.data.success) {
        const images = response.data.data.images || [];
        setSerperImages(images);

        // Chọn ảnh đầu tiên nếu có
        if (images.length > 0) {
          const firstImage = images[0].imageUrl;
          setGeneratedPost((prevPost) => ({
            ...prevPost,
            image: firstImage,
          }));

          // Cập nhật trong mảng generatedPosts
          setGeneratedPosts((prevPosts) => {
            const updatedPosts = [...prevPosts];
            if (updatedPosts[selectedPostIndex]) {
              updatedPosts[selectedPostIndex] = {
                ...updatedPosts[selectedPostIndex],
                image: firstImage,
              };
            }
            return updatedPosts;
          });
        }
      } else {
        toast.warning("Không thể lấy ảnh từ Serper. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Lỗi khi lấy ảnh từ Serper:", error);
      toast.error("Đã xảy ra lỗi khi lấy ảnh. Vui lòng thử lại sau.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  /**
   * Generate an image using Gemini AI
   * @param {string} promptText - Prompt text
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  const generateAIImage = async (promptText, userId) => {
    setIsRegeneratingImage(true);
    try {
      const response = await generateGeminiImage(promptText, userId);

      if (response.data.success) {
        const imageUrl = response.data.data.imageUrl;
        if (imageUrl) {
          setGeminiGeneratedImage(imageUrl);
          setGeneratedPost((prevPost) => ({
            ...prevPost,
            image: imageUrl,
          }));

          // Cập nhật trong mảng generatedPosts
          setGeneratedPosts((prevPosts) => {
            const updatedPosts = [...prevPosts];
            if (updatedPosts[selectedPostIndex]) {
              updatedPosts[selectedPostIndex] = {
                ...updatedPosts[selectedPostIndex],
                image: imageUrl,
              };
            }
            return updatedPosts;
          });
        } else {
          toast.warning(
            "Không thể tạo ảnh với AI. Vui lòng thử lại hoặc sử dụng ảnh từ web.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        }
      } else {
        toast.warning("Không thể tạo ảnh với AI. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Lỗi khi tạo ảnh bằng Gemini:", error);

      // Xử lý các lỗi liên quan đến giới hạn ảnh AI
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.error === "USER_AI_IMAGE_LIMIT_REACHED") {
          toast.error(
            `Bạn đã đạt giới hạn tạo ảnh AI hôm nay (${errorData.limitInfo?.userCount}/${errorData.limitInfo?.userLimit}). Vui lòng quay lại vào ngày mai.`,
            {
              position: "top-right",
              autoClose: 7000,
            }
          );
        } else if (errorData.error === "GLOBAL_AI_IMAGE_LIMIT_REACHED") {
          toast.error(
            "Hệ thống đã đạt giới hạn tạo ảnh AI trong ngày. Vui lòng quay lại vào ngày mai.",
            {
              position: "top-right",
              autoClose: 7000,
            }
          );
        } else if (errorData.error === "GLOBAL_AI_IMAGE_LIMIT_DISABLED") {
          toast.error(
            "Tính năng tạo ảnh AI đã bị vô hiệu hóa do vượt quá giới hạn hệ thống. Vui lòng quay lại vào ngày mai.",
            {
              position: "top-right",
              autoClose: 7000,
            }
          );
        } else if (errorData.message) {
          toast.error(errorData.message, {
            position: "top-right",
            autoClose: 5000,
          });
        } else {
          toast.error("Đã xảy ra lỗi khi tạo ảnh. Vui lòng thử lại sau.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } else {
        toast.error("Đã xảy ra lỗi khi tạo ảnh. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  /**
   * Regenerate post content
   * @param {Object} options - Post generation options
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  const regeneratePost = async (options, userId) => {
    if (!checkPostLimit()) return;

    setIsRegeneratingPost(true);

    try {
      const response = await generatePost(options, userId);

      if (response.data.success) {
        // Kiểm tra cấu trúc response mới
        let posts = [];
        if (response.data.posts && Array.isArray(response.data.posts)) {
          // Cấu trúc mới: posts là trường trong response.data
          posts = response.data.posts;
        } else if (
          response.data.data &&
          response.data.data.posts &&
          Array.isArray(response.data.data.posts)
        ) {
          // Cấu trúc mới: posts là trường trong response.data.data
          posts = response.data.data.posts;
        } else if (
          response.data.data &&
          (response.data.data.title || response.data.data.content)
        ) {
          // Cấu trúc cũ: một bài viết duy nhất
          const { title, content } = response.data.data;
          posts = [
            {
              title: title || "",
              content: content || "",
              imageUrls: response.data.data.imageUrls || [],
            },
          ];
        } else {
          console.error(
            "Cấu trúc dữ liệu không đúng định dạng:",
            response.data
          );
          throw new Error("Dữ liệu không đúng định dạng");
        }

        // Lưu trữ các bài viết đã tạo
        const formattedPosts = posts.map((post) => ({
          text: post.content,
          title: post.title || post.content.substring(0, 50),
          image:
            post.imageUrls && post.imageUrls.length > 0
              ? post.imageUrls[0]
              : "",
        }));

        setGeneratedPosts(formattedPosts);

        // Đặt bài viết đầu tiên làm bài viết hiện tại
        if (formattedPosts.length > 0) {
          setGeneratedPost(formattedPosts[0]);
          setEditedPostText(formattedPosts[0].text);
          setSelectedPostIndex(0);
        }

        // Đặt chế độ sửa về false
        setIsEditMode(false);
        setHasEdited(false);

        // Auto-save regenerated post
        autoSaveGeneratedPost(
          {
            contextFiles: [],
            allowNoImage: false,
            imageSource: "internet",
          },
          {},
          formattedPosts,
          "normal"
        ).then((result) => {
          if (result.success) {
            setAutoSavePostId(result.postId);
            console.log("✅ Bài viết tái tạo được tự động lưu:", result.postId);
          }
        }).catch((error) => {
          console.error("❌ Lỗi khi auto-save bài viết tái tạo:", error);
        });

        // Thông báo thành công
        if (formattedPosts.length > 1) {
          toast.success(
            `Đã tạo mới ${formattedPosts.length} phiên bản bài viết thành công!`,
            {
              position: "top-right",
              autoClose: 3000,
            }
          );
        } else {
          toast.success("Đã tạo mới nội dung bài viết thành công!", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        toast.error(
          response.data.message ||
            "Không thể tái tạo bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Lỗi khi tái tạo bài viết:", error);

      // Kiểm tra nếu lỗi là do đạt giới hạn số lần tạo bài viết
      if (
        error.response &&
        error.response.data &&
        error.response.data.error === "DAILY_LIMIT_REACHED"
      ) {
        toast.error(
          error.response.data.message ||
            "AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
      // Xử lý lỗi từ validateUserSubscription
      else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần đăng ký gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION_EXPIRY"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần gia hạn gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "SUBSCRIPTION_EXPIRED"
      ) {
        toast.error(
          error.response.data.message ||
            "Gói dịch vụ của bạn đã hết hạn, vui lòng gia hạn.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.message &&
        error.message.includes("Không thể lấy thông tin từ URL tham khảo")
      ) {
        // Hiển thị thông báo lỗi cụ thể về URL tham khảo
        toast.error(error.message, {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        error.response.data.message.includes("URL")
      ) {
        // Hiển thị thông báo lỗi từ backend về URL
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (
        error.message &&
        error.message ===
          "Error: AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo"
      ) {
        // Hiển thị thông báo lỗi cụ thể về giới hạn bài viết
        toast.error(
          "AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        // Hiển thị thông báo lỗi cụ thể nếu có, nếu không thì hiển thị thông báo chung
        toast.error(
          error.message ||
            "Đã xảy ra lỗi khi tái tạo bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } finally {
      setIsRegeneratingPost(false);
    }
  };

  /**
   * Regenerate images for the post
   * @param {string} userId - User ID
   * @param {boolean} allowNoImage - Whether to allow no image
   * @param {string} imageSource - Image source
   * @returns {Promise<void>}
   */
  const regenerateImages = async (userId, allowNoImage, imageSource) => {
    if (!generatedPost) {
      toast.error("Vui lòng tạo bài viết trước khi tạo lại ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (hasEdited) {
      if (
        !window.confirm(
          "Tạo lại ảnh có thể ảnh hưởng đến nội dung bài viết đã chỉnh sửa. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }

    if (!checkPostLimit()) return;

    setIsRegeneratingImage(true);

    // Xử lý dựa trên nguồn ảnh
    if (imageSource === "internet") {
      // Nếu nguồn ảnh là internet, gọi API lấy ảnh từ Serper
      await fetchSerperImages(
        generatedPost.title || generatedPost.text,
        userId
      );
    } else if (imageSource === "ai") {
      // Nếu nguồn ảnh là AI, gọi API tạo ảnh bằng Gemini
      await generateAIImage(generatedPost.title || generatedPost.text, userId);
    } else if (imageSource === "upload") {
      // Nếu nguồn ảnh là upload, không làm gì cả
      setIsRegeneratingImage(false);
      toast.info(
        "Không thể tạo lại ảnh khi sử dụng ảnh từ thiết bị. Vui lòng tải lên ảnh mới.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } else {
      setIsRegeneratingImage(false);
    }

    try {
      // ... existing code ...
    } catch (error) {
      console.error("Lỗi khi tái tạo ảnh:", error);

      // Kiểm tra nếu lỗi là do đạt giới hạn số lần tạo bài viết
      if (
        error.response &&
        error.response.data &&
        error.response.data.error === "DAILY_LIMIT_REACHED"
      ) {
        toast.error(
          error.response.data.message ||
            "AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
      // Xử lý lỗi từ validateUserSubscription
      else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần đăng ký gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "NO_SUBSCRIPTION_EXPIRY"
      ) {
        toast.error(
          error.response.data.message ||
            "Bạn cần gia hạn gói dịch vụ để sử dụng tính năng này.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === "SUBSCRIPTION_EXPIRED"
      ) {
        toast.error(
          error.response.data.message ||
            "Gói dịch vụ của bạn đã hết hạn, vui lòng gia hạn.",
          {
            position: "top-right",
            autoClose: 5000,
            onClick: redirectToUpgrade,
          }
        );
      } else if (
        error.message &&
        error.message ===
          "Error: AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo"
      ) {
        // Hiển thị thông báo lỗi cụ thể về giới hạn bài viết
        toast.error(
          "AIContent nghi ngờ bạn đang dùng công cụ tự động can thiệp hệ thống của chúng tôi, tài khoản của bạn có thể sử dụng lại từ 0h01 ngày tiếp theo",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        // Hiển thị thông báo lỗi cụ thể nếu có, nếu không thì hiển thị thông báo chung
        toast.error(
          error.message ||
            "Đã xảy ra lỗi khi tái tạo ảnh. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    }
  };

  // Toggle edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  const toggleEditMode = () => {
    if (isEditMode) {
      // Exiting edit mode, update the post with edited text
      setGeneratedPost((prevPost) => ({
        ...prevPost,
        text: editedPostText,
      }));

      // Cập nhật trong mảng generatedPosts
      setGeneratedPosts((prevPosts) => {
        const updatedPosts = [...prevPosts];
        if (updatedPosts[selectedPostIndex]) {
          updatedPosts[selectedPostIndex] = {
            ...updatedPosts[selectedPostIndex],
            text: editedPostText,
          };
        }
        return updatedPosts;
      });
    } else {
      // Entering edit mode, make sure editedPostText is up to date
      setEditedPostText(generatedPost?.text || "");
    }
    setIsEditMode(!isEditMode);
  };

  return {
    isGeneratingPost,
    isRegeneratingPost,
    isRegeneratingImage,
    generatedPost,
    editedPostText,
    setEditedPostText,
    hasEdited,
    setHasEdited,
    serperImages,
    selectedSerperImages,
    setSelectedSerperImages,
    geminiGeneratedImage,
    isEditMode,
    toggleEditMode,
    generateNewPost,
    regeneratePost,
    regenerateImages,
    fetchSerperImages,
    generateAIImage,
    checkPostLimit,
    // Thêm các function và state mới
    generatedPosts,
    selectedPostIndex,
    selectPost,
    redirectToUpgrade,
    // Auto-save tracking
    autoSavePostId,
    setAutoSavePostId,
  };
};

export default usePostGeneration;
